
import React, { useState } from 'react';
import { SimulationConfig, MidiNoteAction } from './types';
import { SectionLabel } from './SectionLabel';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { AUDIO } from './services/AudioService';
import { ControlGroup } from './ControlGroup';

// Subcomponents
import { MidiPitchHistogram } from './components/settings/rhythmic/MidiPitchHistogram';
import { MultiSimControls } from './components/settings/rhythmic/MultiSimControls';
import { SimulationEditor } from './components/settings/rhythmic/SimulationEditor';

interface RhythmicMidiFilterSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const RhythmicMidiFilterSection: React.FC<RhythmicMidiFilterSectionProps> = ({ config, setConfig }) => {
  const customMelody = config.customMelodies.find(m => m.id === config.selectedMelody || m.name === config.selectedMelody);
  const isMidi = customMelody?.format === 'MIDI' && customMelody.midiData;
  const tracks = isMidi ? AUDIO.getMidiTracks(customMelody.midiData!) : [];
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [editingSimId, setEditingSimId] = useState<string | null>(null);

  if (config.mode !== 'RHYTHMIC' || !isMidi) return null;

  const noteCount = customMelody!.midiData!.notes.length;
  if (noteCount === 0) {
    return (
      <div className="flex flex-col gap-3 p-4 rounded-3xl border bg-red-500/5 border-red-500/20">
        <SectionLabel icon="fa-music-slash" darkMode={config.darkMode}>MIDI Data Empty</SectionLabel>
        <p className="text-[11px] text-red-400 font-bold leading-relaxed">
          No valid notes were found in this MIDI file or Online Sequencer string. 
        </p>
      </div>
    );
  }

  const toggleTrack = (trackIndex: number) => {
    setConfig(p => {
      const current = p.rhythmicMidiFilteredTracks || [];
      const next = current.includes(trackIndex) ? current.filter(t => t !== trackIndex) : [...current, trackIndex];
      return { ...p, rhythmicMidiFilteredTracks: next };
    });
  };

  const toggleBackgroundTrack = (trackIndex: number) => {
    setConfig(p => {
      const current = p.rhythmicMidiBackgroundTracks || [];
      const next = current.includes(trackIndex) ? current.filter(t => t !== trackIndex) : [...current, trackIndex];
      return { ...p, rhythmicMidiBackgroundTracks: next };
    });
  };

  const toggleMuteTrack = (trackIndex: number) => {
    setConfig(p => {
      const current = p.rhythmicMidiMutedTracks || [];
      const next = current.includes(trackIndex) ? current.filter(t => t !== trackIndex) : [...current, trackIndex];
      return { ...p, rhythmicMidiMutedTracks: next };
    });
  };

  const applyPreset = (type: string) => {
    setConfig(p => {
        const next = { ...p };
        switch(type) {
            case 'MAIN':
                next.rhythmicMidiMinVelocity = 60;
                next.rhythmicMidiMaxVelocity = 127;
                next.rhythmicMidiMinPitch = 40;
                next.rhythmicMidiMaxPitch = 100;
                break;
            case 'BASS':
                next.rhythmicMidiMinPitch = 0;
                next.rhythmicMidiMaxPitch = 50;
                break;
            case 'HIGH':
                next.rhythmicMidiMinPitch = 70;
                next.rhythmicMidiMaxPitch = 127;
                break;
            case 'PERCUSSION':
                const percTrack = tracks.find(t => t.name.toLowerCase().includes('perc') || t.name.toLowerCase().includes('drum'));
                if (percTrack) next.rhythmicMidiFilteredTracks = [percTrack.index];
                break;
            case 'ALL':
                next.rhythmicMidiMinPitch = 0;
                next.rhythmicMidiMaxPitch = 127;
                next.rhythmicMidiMinVelocity = 0;
                next.rhythmicMidiMaxVelocity = 127;
                next.rhythmicMidiFilteredTracks = [];
                next.rhythmicMidiMutedTracks = [];
                break;
        }
        return next;
    });
  };

  const previewFiltered = () => {
    if (isPreviewing) return;
    setIsPreviewing(true);
    const allNotes = customMelody!.midiData!.notes;
    const notes = allNotes.filter((n: any) => AUDIO.getNoteAction(n, config) >= MidiNoteAction.PLAY);
    if (notes.length === 0) { setIsPreviewing(false); return; }
    const firstNoteTime = notes[0].time;
    const previewDuration = 5.0;
    const previewNotes = notes
        .filter((n: any) => n.time - firstNoteTime < previewDuration)
        .map((n: any) => ({
            midi: n.midi,
            velocity: n.velocity,
            time: n.time - firstNoteTime,
            duration: config.rhythmicMidiUseExactDuration ? n.duration : undefined
        }));
    AUDIO.playMidiNotes(previewNotes, config);
    setTimeout(() => setIsPreviewing(false), previewDuration * 1000 + 500);
  };

  return (
    <div className="space-y-4 p-4 bg-white/5 rounded-xl border border-white/10 mt-4">
      <div className="flex items-center justify-between">
        <SectionLabel icon="fa-music" darkMode={config.darkMode}>MIDI BOUNCE FILTERING</SectionLabel>
        <button 
            onClick={previewFiltered} disabled={isPreviewing}
            className={`px-3 py-1 rounded-lg text-[10px] font-bold transition-all flex items-center gap-2 ${isPreviewing ? 'bg-white/10 text-white/20' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30'}`}
        >
            <i className={`fa-solid ${isPreviewing ? 'fa-spinner fa-spin' : 'fa-play'}`}></i>
            PREVIEW
        </button>
      </div>
      
      <div className="flex flex-wrap gap-2 mb-2">
        {['MAIN', 'BASS', 'HIGH', 'PERCUSSION', 'ALL'].map(t => (
            <button key={t} onClick={() => applyPreset(t)} className="px-2 py-1 rounded bg-white/5 text-[10px] font-bold text-white/60 hover:bg-white/10 uppercase tracking-tighter">{t}</button>
        ))}
      </div>

      <div className="space-y-3">
        <ControlGroup label="Enable Filtering"><Toggle active={config.rhythmicMidiFilterEnabled} onClick={() => setConfig({ ...config, rhythmicMidiFilterEnabled: !config.rhythmicMidiFilterEnabled })} /></ControlGroup>
        <ControlGroup label="Play Background Tracks"><Toggle active={config.rhythmicMidiPlayBackground} onClick={() => setConfig({ ...config, rhythmicMidiPlayBackground: !config.rhythmicMidiPlayBackground })} /></ControlGroup>
        <ControlGroup label="Use Exact MIDI Duration"><Toggle active={config.rhythmicMidiUseExactDuration} onClick={() => setConfig({ ...config, rhythmicMidiUseExactDuration: !config.rhythmicMidiUseExactDuration })} /></ControlGroup>
      </div>

      {config.rhythmicMidiFilterEnabled && (
        <div className="space-y-4 pt-2">
            <MidiPitchHistogram notes={customMelody!.midiData!.notes} minPitch={config.rhythmicMidiMinPitch} maxPitch={config.rhythmicMidiMaxPitch} darkMode={config.darkMode} config={config} />

            <div className="space-y-4">
                <div className="space-y-2">
                    <p className="text-[10px] text-blue-400/60 uppercase">Bounce Tracks</p>
                    <div className="flex flex-wrap gap-2">
                        {tracks.map(t => (
                        <button key={t.index} onClick={() => toggleTrack(t.index)} className={`px-3 py-1 rounded-full text-[10px] font-bold ${config.rhythmicMidiFilteredTracks.includes(t.index) ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/40'}`}>{t.name}</button>
                        ))}
                    </div>
                </div>

                {config.rhythmicMidiPlayBackground && (
                    <div className="space-y-2">
                        <p className="text-[10px] text-purple-400/60 uppercase">Background Tracks</p>
                        <div className="flex flex-wrap gap-2">
                            {tracks.map(t => (
                            <button key={t.index} onClick={() => toggleBackgroundTrack(t.index)} className={`px-3 py-1 rounded-full text-[10px] font-bold ${(config.rhythmicMidiBackgroundTracks || []).includes(t.index) ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/40'}`}>{t.name}</button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-4">
                <SmartInput label="Min Velocity" value={config.rhythmicMidiMinVelocity} onChange={(v) => setConfig({ ...config, rhythmicMidiMinVelocity: v })} min={0} max={127} darkMode={config.darkMode} />
                <SmartInput label="Max Velocity" value={config.rhythmicMidiMaxVelocity} onChange={(v) => setConfig({ ...config, rhythmicMidiMaxVelocity: v })} min={0} max={127} darkMode={config.darkMode} />
                <SmartInput label="Min Pitch" value={config.rhythmicMidiMinPitch} onChange={(v) => setConfig({ ...config, rhythmicMidiMinPitch: v })} min={0} max={127} darkMode={config.darkMode} />
                <SmartInput label="Max Pitch" value={config.rhythmicMidiMaxPitch} onChange={(v) => setConfig({ ...config, rhythmicMidiMaxPitch: v })} min={0} max={127} darkMode={config.darkMode} />
            </div>
        </div>
      )}

      <MultiSimControls config={config} setConfig={setConfig} editingSimId={editingSimId} setEditingSimId={setEditingSimId} tracks={tracks} />
      
      {editingSimId && (
          <SimulationEditor 
            config={config} setConfig={setConfig} 
            sim={config.rhythmicSimulations.find(s => s.id === editingSimId)} 
            simIdx={config.rhythmicSimulations.findIndex(s => s.id === editingSimId)} 
          />
      )}
    </div>
  );
};
